import React, { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import Logger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { ROUTES } from 'src/constants/route';
import { getStatusCodeFromError } from 'src/utils/error';
import ErrorView from '../ErrorView';

const domManipulationErrorMessage = "Failed to execute 'removeChild' on 'Node'";

// Matches ASCII control characters (0x00-0x1F, 0x7F) that have no valid use in URL paths.
// These only appear from security scanners or injection attempts.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_REGEX = /[\x00-\x1f\x7f]/;

/**
 * Checks if the current URL contains control characters that break React Router's
 * regex-based route matching (which uses `.` that doesn't match \r, \n, etc.).
 */
const hasControlCharsInPath = (): boolean => {
  try {
    return CONTROL_CHARS_REGEX.test(decodeURIComponent(window.location.pathname));
  } catch {
    // decodeURIComponent can throw on malformed sequences (e.g. %ZZ) — also malformed
    return true;
  }
};

/**
 * Error element for React Router that handles and logs errors while providing a user-friendly error display.
 * This component is designed to be used with React Router's errorElement prop.
 */
const RouterError: React.FC = () => {
  const error = useRouteError() as Error & { status?: number | string };

  useEffect(() => {
    // Silently redirect for malformed URLs containing control characters.
    // These break React Router's route matching regex and are never legitimate user traffic.
    if (hasControlCharsInPath()) {
      Logger.warn(ClientLog.IllegalInternalDataState, {
        message: 'Malformed URL with control characters detected',
        href: window.location.href,
      });
      const params = new URLSearchParams(window.location.search);
      const locale = params.get('mons_sel_locale');
      const redirectUrl = locale
        ? `${ROUTES.NEW_LEARN.BASE}?mons_sel_locale=${encodeURIComponent(locale)}`
        : ROUTES.NEW_LEARN.BASE;
      window.location.replace(redirectUrl);
      return;
    }

    const logContext = {
      errorInfo: error.stack,
      href: window.location.href,
    };

    if (error.message?.includes(domManipulationErrorMessage)) {
      Logger.error(ClientLog.DOMManipulationException, error, logContext);
    } else {
      Logger.error(ClientLog.ComponentUnhandledException, error, logContext);
    }
  }, [error]);

  // Render nothing while redirecting for malformed URLs
  if (hasControlCharsInPath()) {
    return null;
  }

  return (
    <div>
      <ErrorView statusCode={getStatusCodeFromError(error)} />
      {process.env.NODE_ENV !== 'production' && (
        <details style={{ whiteSpace: 'pre-wrap' }}>
          <summary>Error Details</summary>
          <p>{error.message && error.message.toString()}</p>
          <p>{error.stack}</p>
        </details>
      )}
    </div>
  );
};

export default RouterError;
